import React from "react";
import { motion } from "framer-motion";
import Banner from "../components/allbanner";
import {
  MessageCircle
} from "lucide-react";

// 🖼️ Import your actual images
import CustomerRelationLogo from "../images/custlogo.png";
import CustomerRelationChart from "../images/chart3.png";
import Activity1 from "../images/cr1.jpg";
import Activity2 from "../images/cr2.jpg";
import Activity3 from "../images/cr3.jpg";

export default function CustomerRelations() {
  return (
    <div className="bg-gray-50 text-gray-800 overflow-hidden">
      {/* ===== Banner ===== */}
      <Banner
        title="Customer Relations Department"
        description="At Isuzu Ravi Motors, we strengthen customer trust through care, transparency, and professionalism — ensuring every customer drives with confidence."
      />

      {/* ===== Our Mission + Logo Section ===== */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
        {/* Left: Mission Text */}
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl font-extrabold text-gray-900 border-l-4 border-[#D71920] pl-3 mb-6">
            Our Mission
          </h2>
          <p className="text-lg leading-relaxed text-gray-700 mb-4">
            Our mission is to build and nurture strong, long-lasting
            relationships with our customers by delivering exceptional service,
            proactive communication, and tailored solutions. We are dedicated to
            understanding and exceeding customer expectations, resolving
            concerns promptly, and fostering trust and loyalty.
          </p>
          <p className="text-lg leading-relaxed text-gray-700">
            Through empathy, integrity, and continuous improvement, we aim to
            create a customer experience that reflects our commitment to
            excellence and mutual success.
          </p>
        </motion.div>

        {/* Right: Logo */}
        <motion.div
          initial={{ opacity: 0, x: 60 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="flex justify-center"
        >
          <img
            src={CustomerRelationLogo}
            alt="Customer Relations Logo"
            className="w-80 h-auto object-contain drop-shadow-xl"
          />
        </motion.div>
      </section>

      {/* ===== Authentic Customer Care ===== */}
      <section className="bg-[#FFF5F5] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-3xl font-extrabold text-gray-900 mb-10 border-l-4 border-[#D71920] pl-3 inline-block"
          >
            Authentic Customer Care
          </motion.h2>

          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            <p>
              Customer Relationship Management is the cornerstone of our
              operations — a commitment to maintaining and deepening meaningful
              bonds with every customer. At Isuzu Ravi Motors, we strive to
              build personal and emotional connections to truly understand our
              customers’ needs and serve them better every day.
            </p>
            <p>
              Our Customer Relations Department values transparency,
              responsiveness, and empathy. We carefully listen to every
              concern, suggestion, and query — ensuring timely action and
              reliable solutions that strengthen customer confidence in our
              brand.
            </p>
            <p>
              Our well-trained and cooperative team continuously monitors every
              detail to ensure customer satisfaction that extends far beyond
              service visits. Each interaction reflects our professionalism and
              commitment to long-term relationships built on trust and care.
            </p>
          </div>
        </div>
      </section>

      {/* ===== Chart Section ===== */}
      <section className="py-20">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto px-6 text-center"
        >
          <h2 className="text-3xl font-extrabold text-gray-900 mb-10 border-l-4 border-[#D71920] pl-3 inline-block">
            Customer Relations Process Chart
          </h2>
          <img
            src={CustomerRelationChart}
            alt="Customer Relations Chart"
            className="rounded-2xl shadow-2xl mx-auto w-full max-w-3xl object-contain"
          />
        </motion.div>
      </section>

      {/* ===== Responsibilities ===== */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <motion.h2
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-3xl font-extrabold text-gray-900 mb-10 border-l-4 border-[#D71920] pl-3 inline-block"
        >
          Customer Relations Support & Responsibilities
        </motion.h2>

        <ul className="list-disc list-inside text-lg text-gray-700 space-y-4 leading-relaxed">
          <li>
            Providing a one-window operation to resolve all customer inquiries
            and issues efficiently.
          </li>
          <li>
            Ensuring swift handling of complaints and inquiries through fair,
            transparent, and professional communication.
          </li>
          <li>
            Making the “Voice of the Customer” the foundation for continued
            service improvement and customer retention.
          </li>
          <li>
            Building a positive brand image by keeping customers informed and
            engaged at every stage of their journey.
          </li>
          <li>
            Supporting our sales and service teams by maintaining strong
            customer feedback and trust mechanisms.
          </li>
        </ul>
      </section>

      {/* ===== Activities ===== */}
      <section className="py-20 bg-[#FFF5F5]">
        <div className="max-w-6xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-3xl font-extrabold text-gray-900 mb-10 border-l-4 border-[#D71920] pl-3 inline-block"
          >
            Customer Relations Activities
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[Activity1, Activity2, Activity3].map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className="rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden bg-white"
              >
                <img
                  src={img}
                  alt={`Customer Relations Activity ${i + 1}`}
                  className="w-full h-72 object-cover"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
 <section className="py-20 text-center bg-white">
        <h2 className="text-3xl font-extrabold text-gray-900 mb-4 border-l-4 border-[#D71920] pl-3 inline-block">
          Your Feedback Matters
        </h2>
        <p className="text-lg text-gray-700 mb-8">
          Help us improve your experience by sharing your valuable feedback.
        </p>

        <a
          href="https://feedback.isuzuravi.com"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-[#D71920] hover:bg-red-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg transition-all"
        >
          Submit Feedback
        </a>
      </section>
      {/* ===== Closing Section ===== */}
      <section className="bg-[#D71920] text-white py-20 text-center">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto space-y-6 px-6"
        >
          <h2 className="text-4xl font-bold">
            Your Satisfaction Drives Our Passion
          </h2>
          <p className="text-lg leading-relaxed">
            At Isuzu Ravi Motors, customer relations is not just a department —
            it’s a promise. We are committed to serving every customer with
            honesty, efficiency, and dedication, ensuring your experience with
            us is as strong and reliable as our vehicles.
          </p>
          

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-10">
            {/* Contact Button */}
            <a
              href="/contact"
              className="bg-white text-[#D71920] font-bold px-8 py-3 rounded-xl shadow-lg hover:bg-gray-100 transition-all inline-flex items-center gap-2"
            >
              Get in Touch
            </a>

            {/* WhatsApp Button */}
            <a
              href={`https://wa.me/923103724444?text=${encodeURIComponent(
                "Hello! I’d like to get assistance from Isuzu Ravi Motors."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-3 rounded-xl shadow-lg transition-all inline-flex items-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp Us
            </a>


          </div>
        </motion.div>
      </section>

     


    </div>

  );
}
